import { api } from '@xtnd/web/shared/api';
import type { DocumentType, StoredDocument, StoredReceipt } from '../types';

class DocumentApi {
  private readonly baseSource = '/juniper';

  /**
   * Get a signed URL for downloading a document from S3
   * @param juniperId Juniper transaction ID
   * @param documentType Type of document (invoice, ledger, receipt)
   * @param filename Filename (required for receipts)
   * @returns Signed URL response with 15-minute expiration
   */
  async getSignedUrl(
    juniperId: string,
    documentType: DocumentType,
    filename?: string
  ): Promise<{
    signedUrl: string;
    filename: string;
    expiresIn: number;
    documentType: string;
  }> {
    // For receipts, filename is required
    if (documentType === 'receipt' && !filename) {
      throw new Error('Filename is required for receipt documents');
    }

    // Build endpoint - filename is optional for invoice/ledger (uses 'default')
    const filenameParam = filename || 'default';
    const endpoint = `${this.baseSource}/${juniperId}/documents/${documentType}/${filenameParam}`;

    try {
      const response = await api.get<{
        signedUrl: string;
        filename: string;
        expiresIn: number;
        documentType: string;
      }>(endpoint);
      return response;
    } catch (error) {
      console.error('Failed to get signed URL:', error);
      throw new Error(
        `Failed to get signed URL for ${documentType}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get MIME type from filename extension
   * @param filename Document filename
   * @returns MIME type string
   */
  getMimeType(filename: string): string {
    const ext = filename.toLowerCase().split('.').pop();

    const mimeTypes: Record<string, string> = {
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      txt: 'text/plain',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
    };

    return mimeTypes[ext || ''] || 'application/octet-stream';
  }

  /**
   * Format file size for display
   * @param bytes File size in bytes
   * @returns Formatted string (e.g., "1.5 MB")
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Get all stored documents for a transaction
   * Extracts document metadata from transaction.documents.storedAssets
   * @param transaction Juniper transaction object
   * @returns Object with PDFs and receipts
   */
  getStoredDocuments(transaction: any): {
    invoice?: StoredDocument;
    ledger?: StoredDocument;
    receipts: StoredReceipt[];
  } {
    const storedAssets = transaction?.documents?.storedAssets;

    return {
      invoice: storedAssets?.pdfs?.invoice,
      ledger: storedAssets?.pdfs?.ledger,
      receipts: storedAssets?.receipts || [],
    };
  }

  /**
   * Check if a transaction has any stored documents available
   * @param transaction Juniper transaction object
   * @returns True if any documents are stored in S3
   */
  hasStoredDocuments(transaction: any): boolean {
    const docs = this.getStoredDocuments(transaction);
    return !!(docs.invoice || docs.ledger || docs.receipts.length > 0);
  }
}

export const documentApi = new DocumentApi();

